import useAudioEngineActions from '@/actions/useAudioEngineActions'
import { DialogBody, DialogTitle } from '@/components/Dialog'
import * as Constants from '@/services/Constants'
import { SpeakerSelect } from '@/components/SpeakerSelect'
import { TalkbackMicSelect } from '@/components/TalkbackMicSelect'
import useAudioSettingsStoreActions from '@/core/useAudioSettingsStoreActions'
import { AudioOutputSettings, AudioSourceInfo } from '@/UserContext'
import HeadphonesIcon from '@/CustomIcons/HeadphonesIcon'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import PlayIcon from '@/CustomIcons/PlayIcon'
import SpeakerIcon from '@/CustomIcons/SpeakerIcon'
import StopIcon from '@/CustomIcons/StopIcon'
import { Button } from '@/UIComponents2/Button'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import { useEffect, useState } from 'react'
import { Card } from '../../Card'
import { CardHeader } from '../../CardHeader'
import { getAudioEngineInstance } from '@/core/AudioEngine'
import { env } from '@/services/EnvironmentVariables'
import useMediaSelectionViews from '@/actions/useMediaSelectionViews'
import { Alert } from '@/UIComponents2/Alert'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'

export function BasicSection() {
    const [playingTest, setPlayingTest] = useState(false)
    const [showSpeakerSettings, setShowSpeakerSettings] = useState(false)
    const { audioOutputSelectionInfo } = useMediaSelectionViews()

    const { getAudioOutputSettings } = useAudioSettingsStoreActions()

    const [initialOutputSettings, setInitialOutputSettings] = useState<AudioOutputSettings | null>(null)
    useEffect(() => {
        if (showSpeakerSettings && audioOutputSelectionInfo) {
            const s = getAudioOutputSettings(audioOutputSelectionInfo.deviceId)
            if (s) {
                setInitialOutputSettings(s)
            }
        } else {
            setInitialOutputSettings(null)
        }
    }, [showSpeakerSettings, audioOutputSelectionInfo, getAudioOutputSettings])

    return (
        <Card className="flex flex-col gap-[20px]">
            <CardHeader icon={HeadphonesIcon} title="Audio" />
            <div className="flex-1 min-h-0 grid grid-cols-[1fr] md:grid-cols-[min-content_1fr] gap-y-[16px] gap-x-[40px]">
                <div className="flex items-center gap-[10px]">
                    <MicrophoneIcon style={{ fontSize: '1.3rem' }} />
                    <span>Microphone</span>
                </div>
                <TalkbackMicSelect className="min-w-[280px] max-w-[500px] md:justify-self-end" />

                <div className="flex items-center gap-[10px]">
                    <SpeakerIcon style={{ fontSize: '1.3rem' }} />
                    <span>Speakers</span>
                </div>
                <SpeakerSelect
                    className="max-w-[500px] md:justify-self-end"
                    onOpenSettings={() => setShowSpeakerSettings(true)}
                    playingTest={playingTest}
                    onTestEnded={() => setPlayingTest(false)}
                />

                <div></div>
                <div className="flex justify-center">
                    <Button
                        onClick={() => setPlayingTest(!playingTest)}
                        className="t-base-light flex items-center gap-1 opacity-75 hover:opacity-100"
                    >
                        {playingTest ? <StopIcon /> : <PlayIcon />}
                        {playingTest ? 'Stop test' : 'Test speaker'}
                    </Button>
                </div>
            </div>
            {audioOutputSelectionInfo && initialOutputSettings && (
                <SpeakerSettings
                    open={showSpeakerSettings}
                    mediaSource={audioOutputSelectionInfo}
                    initialSettings={initialOutputSettings}
                    onClose={() => setShowSpeakerSettings(false)}
                />
            )}
        </Card>
    )
}

type SpeakerSettingsProps = {
    open: boolean
    onClose: () => void
    mediaSource: AudioSourceInfo
    initialSettings: AudioOutputSettings
}
function SpeakerSettings(props: SpeakerSettingsProps) {
    const [currSettings, setCurrSettings] = useState<AudioOutputSettings>(props.initialSettings)

    const { setAudioOutputDevice } = useAudioEngineActions()

    const { setAudioOutputSettings } = useAudioSettingsStoreActions()

    const setSettingsHelper = (settingsNext: Partial<AudioOutputSettings>) => {
        setCurrSettings((s) => {
            return { ...s, ...settingsNext }
        })
    }

    const onSubmit = () => {
        setAudioOutputSettings(props.mediaSource!.deviceId, currSettings)

        // not ideal -- wait for the setting to propagate.
        setTimeout(() => {
            setAudioOutputDevice(props.mediaSource!.deviceId)
        }, 500)
        props.onClose()
    }

    if (!props.open) {
        return null
    }

    return (
        <CrossPlatformDialog
            title="Speaker Settings"
            onDefaultClose={() => props.onClose()}
            forceBrowserModal
            browserZIndex={21}
        >
            <DialogBody>
                <div className="flex flex-col gap-4">
                    <DialogTitle noPadding>Speaker Settings</DialogTitle>

                    <SampleRateSection
                        mediaSource={props.mediaSource}
                        settings={currSettings}
                        setSettingsHelper={setSettingsHelper}
                    />

                    <BitDepthSelection settings={currSettings} setSettingsHelper={setSettingsHelper} />

                    <Alert className="my-3" variant="outlined" level="warning">
                        The sample rate and bit depth should match the physical audio output device.
                    </Alert>
                </div>
            </DialogBody>

            <DialogActionButtonsSection
                onLeftButtonClicked={() => props.onClose()}
                onRightButtonClicked={() => onSubmit()}
                leftButtonText="Cancel"
                rightButtonText="Save"
            />
        </CrossPlatformDialog>
    )
}

function SampleRateSection({
    mediaSource,
    settings,
    setSettingsHelper,
}: {
    mediaSource: AudioSourceInfo
    settings: AudioOutputSettings
    setSettingsHelper: (settingsNext: Partial<AudioOutputSettings>) => void
}) {
    const [supportedSampleRates, set_supportedSampleRates] = useState<number[]>(Constants.commonSampleRates)

    // check supported sample rates
    useEffect(() => {
        ;(async () => {
            if (!mediaSource.audioEngineDeviceId) {
                return
            }
            // on osx, checking the sample rate of a device (calling
            // the Pa_IsFormatSupported() in the audio engine) somehow
            // causes it to break the stream.
            if (env.OS === 'darwin') {
                return
            }

            const supported: number[] = await getAudioEngineInstance().checkSupportedSampleRates({
                audioEngineDeviceId: mediaSource.audioEngineDeviceId,
                deviceDirection: 'input',
                sampleRates: Constants.commonSampleRates,
            })
            if (supported.length > 0) {
                set_supportedSampleRates(supported)
            }
        })()
    }, [])

    const onChange = (value: string) => {
        if (value === '_default') {
            setSettingsHelper({ _isDeviceSampleRateOverridden: false })
        } else {
            setSettingsHelper({ sampleRate: parseInt(value), _isDeviceSampleRateOverridden: true })
        }
    }

    const value = settings._isDeviceSampleRateOverridden ? String(settings.sampleRate) : '_default'

    return (
        <SimpleSelect
            label="Sample Rate"
            value={value}
            items={[{ value: '_default', label: 'Device default' }].concat(
                supportedSampleRates.map((value) => {
                    const label = value.toLocaleString() + ' Hz'
                    return { value: String(value), label }
                })
            )}
            onChange={(val) => {
                onChange(val)
            }}
            widthMatching
        />
    )
}

function BitDepthSelection({
    settings,
    setSettingsHelper,
}: {
    settings: AudioOutputSettings
    setSettingsHelper: (settingsNext: Partial<AudioOutputSettings>) => void
}) {
    const handleChange = (value: string) => {
        setSettingsHelper({ bitDepth: parseInt(value) })
    }

    return (
        <SimpleSelect
            label="Bit Depth"
            value={String(settings.bitDepth)}
            items={Constants.commonBitDepths.map((value) => ({ value: String(value), label: `${value} bits` }))}
            onChange={(val) => handleChange(val)}
            widthMatching
        />
    )
}
